import { formatViolationErrorField } from 'Util/formatters'
import { useState } from 'react'

type FieldErrors = Record<string, string[]>

interface ErrorProps {
  error: boolean
  helperText: string
}

interface UseValidationErrorsReturn<T extends { field: string; message: string }> {
  fieldErrors: FieldErrors
  setValidationErrors: (violations: T[] | null) => void
  resetFieldErrors: () => void
  getFieldErrorHelperText: (fieldName: string) => string
  getErrorProps: (fieldName: string) => ErrorProps
}

export function useValidationErrors<T extends { field: string; message: string }>(
  fieldErrorsDefaultState: FieldErrors
): UseValidationErrorsReturn<T> {
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>(fieldErrorsDefaultState)

  function setValidationErrors(violations: T[] | null) {
    if (!violations || violations.length === 0) return

    setFieldErrors(() => {
      const updatedFieldErrors = { ...fieldErrorsDefaultState }
      for (const violation of violations) {
        updatedFieldErrors[formatViolationErrorField(violation.field)] = [violation.message]
      }
      return updatedFieldErrors
    })
  }

  function resetFieldErrors() {
    setFieldErrors(fieldErrorsDefaultState)
  }

  function getFieldErrorHelperText(fieldName: string): string {
    return fieldErrors[fieldName] && fieldErrors[fieldName].length > 0
      ? fieldErrors[fieldName][0]
      : ''
  }

  function getErrorProps(fieldName: string): ErrorProps {
    return {
      error: (fieldErrors[fieldName]?.length ?? 0) > 0,
      helperText: getFieldErrorHelperText(fieldName)
    }
  }

  return {
    fieldErrors,
    setValidationErrors,
    resetFieldErrors,
    getFieldErrorHelperText,
    getErrorProps
  }
}
